import { useInfiniteQuery } from '@tanstack/react-query';
import { ActivityCategory } from '@repo/types';
import { includes, isEmpty, isObject, mapKeys, mapValues, omitBy, upperFirst } from 'lodash';

import { activityLogApi } from '../api/activityLogApi';
import { ActivityChange, ActivityDiff, DiffNode } from '../types/activity';
import { PAGE_SIZE } from '../constants/activity';

/**
 * Fields to exclude from display in the activity log.
 */
const EXCLUDED_FIELDS = [
  'updatedBy',
  'updatedAt',
  'submittedAt',
  'closedAt',
  'createdAt',
  'createdBy',
  'parent',
];

/**
 * Checks if a field should be excluded from display.
 * Excludes: groupkey fields, ID fields, and explicitly listed fields.
 */
const shouldExcludeField = (fieldName: string): boolean => {
  const lowerName = fieldName.toLowerCase();
  return (
    lowerName.includes('groupkey') ||
    lowerName === 'id' ||
    lowerName.endsWith('id') ||
    lowerName.endsWith('ids') ||
    includes(EXCLUDED_FIELDS, fieldName)
  );
};

/**
 * Type guard to check if a value is an ActivityChange.
 */
function isActivityChange(value: unknown): value is ActivityChange {
  return (
    value !== null &&
    typeof value === 'object' &&
    'type' in value &&
    ('old' in value || 'new' in value)
  );
}

/**
 * Filters out excluded fields from an ActivityDiff recursively.
 */
function filterDiff(diff: ActivityDiff | undefined): ActivityDiff | undefined {
  if (!diff) return undefined;

  const filtered = omitBy(
    mapValues(diff, value => {
      if (isActivityChange(value)) return value;
      if (isObject(value)) return filterDiff(value as ActivityDiff);
      return undefined;
    }),
    (_, key) => shouldExcludeField(key as string)
  ) as ActivityDiff;

  return isEmpty(filtered) ? undefined : filtered;
}

/**
 * Maps flat fields to their logical parent group.
 * Fields listed here will be moved from root level into the specified group.
 */
const FIELD_GROUPING: Record<string, string> = {
  appealStatus: 'appeal',
  appealedAmount: 'appeal',
  emailQueueStatus: 'emailQueue',
};

/**
 * Reorganizes diff by moving flat fields into their logical parent groups.
 * For example, `appealStatus` at root level will be moved inside `appeal` group.
 */
function reorganizeDiff(diff: ActivityDiff | undefined): ActivityDiff | undefined {
  if (!diff) return undefined;

  const result: ActivityDiff = {};
  const groups: Record<string, ActivityDiff> = {};

  for (const [key, value] of Object.entries(diff)) {
    const targetGroup = FIELD_GROUPING[key];

    if (targetGroup && isActivityChange(value)) {
      // This field should be moved into a group
      if (!groups[targetGroup]) {
        groups[targetGroup] = {};
      }
      groups[targetGroup][key] = value;
    } else {
      // Keep field at its current level
      result[key] = value;
    }
  }

  // Merge grouped fields into existing groups or create new ones
  for (const [groupName, groupFields] of Object.entries(groups)) {
    if (result[groupName] && isObject(result[groupName]) && !isActivityChange(result[groupName])) {
      // Merge into existing group
      result[groupName] = { ...(result[groupName] as ActivityDiff), ...groupFields };
    } else {
      // Create new group
      result[groupName] = groupFields;
    }
  }

  return isEmpty(result) ? undefined : result;
}

/**
 * Converts an ActivityDiff into a hierarchical array of DiffNode items.
 * Preserves the nested structure for tree-like display.
 */
export function buildDiffTree(diff: ActivityDiff | undefined): DiffNode[] {
  if (!diff) return [];

  const nodes: DiffNode[] = [];

  for (const [key, value] of Object.entries(diff)) {
    if (isActivityChange(value)) {
      // Leaf node - this is an actual change
      nodes.push({
        key,
        change: value,
      });
    } else if (value && typeof value === 'object') {
      // Branch node - this contains nested changes
      const children = buildDiffTree(value as ActivityDiff);
      if (children.length > 0) {
        nodes.push({
          key,
          children,
        });
      }
    }
  }

  return nodes;
}

/**
 * Counts the total number of changes in a diff tree.
 */
export function countChanges(nodes: DiffNode[]): number {
  let count = 0;
  for (const node of nodes) {
    if (node.change) {
      count++;
    }
    if (node.children) {
      count += countChanges(node.children);
    }
  }
  return count;
}

/**
 * Hook for fetching activity logs with cursor-based pagination (lastKey pattern).
 * Uses React Query's useInfiniteQuery for automatic pagination management.
 * Automatically filters out excluded fields from activity diffs.
 *
 * @param eventId - The ID of the event to fetch activity logs for
 * @param category - The category of activity logs (default: INVOICE)
 * @param enabled - Whether the query should be enabled (default: true)
 */
export const useActivityLogs = (
  eventId: string,
  category: ActivityCategory = ActivityCategory.INVOICE,
  enabled: boolean = true
) => {
  return useInfiniteQuery({
    queryKey: ['activityLogs', category, eventId],
    queryFn: async ({ pageParam }) => {
      const response = await activityLogApi.getActivityLogs({
        category,
        eventId,
        limit: PAGE_SIZE,
        lastKey: pageParam ?? undefined,
      });

      return {
        ...response,
        data: response.data
          .map(activity => ({
            ...activity,
            diff: reorganizeDiff(filterDiff(activity.diff)),
          }))
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
      };
    },
    initialPageParam: null as string | null,
    getNextPageParam: lastPage => {
      if (!lastPage.data.length || !lastPage.lastKey) return undefined;
      return lastPage.lastKey;
    },
    enabled: enabled && !!eventId,
    staleTime: 0, // Always refetch when drawer opens
    gcTime: 0, // Don't cache - always get fresh data
    // Re-sort all accumulated pages to ensure global time order
    select: data => {
      const allActivities = data.pages.flatMap(page => page.data);
      const sorted = [...allActivities].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      return {
        ...data,
        pages: data.pages.map((page, index) => ({
          ...page,
          data: index === 0 ? sorted : [],
        })),
      };
    },
  });
};

/**
 * Hook for fetching activity logs from both Invoice and Write-Off categories.
 * Calls activityLogApi.getActivityLogs for invoice and for each write-off,
 * then merges and sorts the results by createdAt in descending order.
 *
 * @param invoiceId - The ID of the invoice to fetch activity logs for
 * @param writeOffIds - Array of write-off IDs to fetch activity logs for
 * @param enabled - Whether the query should be enabled (default: true)
 */
export const useMergedActivityLogs = (
  invoiceId: string,
  writeOffIds: string[] = [],
  enabled: boolean = true
) => {
  return useInfiniteQuery({
    queryKey: ['mergedActivityLogs', invoiceId, writeOffIds],
    queryFn: async ({ pageParam }) => {
      const isFirstPage = !pageParam;
      const invoicePageParam = pageParam?.invoice;
      const writeOffPageParams = pageParam?.writeOffs ?? {};

      const shouldFetchInvoice = isFirstPage || invoicePageParam !== undefined;
      const writeOffIdsToFetch = isFirstPage
        ? writeOffIds
        : writeOffIds.filter(id => writeOffPageParams[id] !== undefined);

      const invoicePromise = shouldFetchInvoice
        ? activityLogApi.getActivityLogs({
            category: ActivityCategory.INVOICE,
            eventId: invoiceId,
            limit: PAGE_SIZE,
            lastKey: invoicePageParam,
          })
        : Promise.resolve({ data: [], lastKey: undefined });
      const writeOffPromises = writeOffIdsToFetch.map(writeOffId =>
        activityLogApi.getActivityLogs({
          category: ActivityCategory.WRITE_OFF,
          eventId: writeOffId,
          limit: PAGE_SIZE,
          lastKey: writeOffPageParams[writeOffId],
        })
      );

      const [invoiceResponse, ...writeOffResponses] = await Promise.all([
        invoicePromise,
        ...writeOffPromises,
      ]);

      const invoiceData = invoiceResponse.data.map(activity => ({
        ...activity,
        diff: reorganizeDiff(filterDiff(activity.diff)),
      }));

      const writeOffData = writeOffResponses.flatMap(response =>
        response.data.map(activity => {
          const filteredDiff = filterDiff(activity.diff);
          const prefixedDiff = filteredDiff
            ? (mapKeys(filteredDiff, (_, key) =>
                key.startsWith('writeOff') ? key : `writeOff${upperFirst(key)}`
              ) as ActivityDiff)
            : undefined;

          return {
            ...activity,
            diff: reorganizeDiff(prefixedDiff),
          };
        })
      );

      const mergedData = [...invoiceData, ...writeOffData].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      const writeOffLastKeys: Record<string, string | undefined> = {};
      writeOffIds.forEach(id => {
        const fetchIndex = writeOffIdsToFetch.indexOf(id);
        if (fetchIndex >= 0) {
          writeOffLastKeys[id] = writeOffResponses[fetchIndex]?.lastKey;
        }
      });

      return {
        data: mergedData,
        lastKey: {
          invoice: invoiceResponse.lastKey,
          writeOffs: writeOffLastKeys,
        },
      };
    },
    initialPageParam: null as {
      invoice?: string;
      writeOffs?: Record<string, string | undefined>;
    } | null,
    getNextPageParam: lastPage => {
      const hasMoreInvoice = !!lastPage.lastKey.invoice;
      const hasMoreWriteOffs = Object.values(lastPage.lastKey.writeOffs).some(key => !!key);

      if (!hasMoreInvoice && !hasMoreWriteOffs) return undefined;

      return {
        invoice: lastPage.lastKey.invoice,
        writeOffs: lastPage.lastKey.writeOffs,
      };
    },
    enabled: enabled && !!invoiceId,
    staleTime: 0, // Always refetch when drawer opens
    gcTime: 0, // Don't cache - always get fresh data
  });
};
